// Cornerstone3D integration for AWS Health Imaging
import {
    RenderingEngine,
    Enums as csEnums,
    imageLoader,
    metaData,
    cache
} from '@cornerstonejs/core';
import {
    ToolGroupManager,
    WindowLevelTool,
    PanTool,
    ZoomTool,
    LengthTool,
    AngleTool,
    ProbeTool,
    EllipticalROITool,
    RectangleROITool,
    BidirectionalTool,
    Enums as ToolEnums
} from '@cornerstonejs/tools';

// Register AHI image loader for Cornerstone3D
export function registerAHIImageLoaderCS3D() {
    // Register the AHI image loader
    imageLoader.registerImageLoader('ahi', loadAHIImageCS3D);
    
    // Register metadata provider for AHI images
    metaData.addProvider(function(type, imageId) {
        if (imageId && typeof imageId === 'string' && imageId.startsWith('ahi:')) {
            const cachedData = window.ahiImageCache && window.ahiImageCache[imageId];
            if (cachedData && cachedData.metadata) {
                const metadata = cachedData.metadata;
                
                if (type === 'imagePlaneModule') {
                    return {
                        pixelSpacing: metadata.pixelSpacing || metadata.PixelSpacing,
                        imageOrientationPatient: metadata.imageOrientationPatient || metadata.ImageOrientationPatient || [1, 0, 0, 0, 1, 0],
                        imagePositionPatient: metadata.imagePositionPatient || metadata.ImagePositionPatient || [0, 0, 0],
                        sliceThickness: metadata.SliceThickness || 1,
                        sliceLocation: metadata.SliceLocation || 0,
                        frameOfReferenceUID: metadata.FrameOfReferenceUID || 'AHI_FRAME_OF_REFERENCE'
                    };
                }
                
                if (type === 'imagePixelModule') {
                    return {
                        rows: metadata.rows || metadata.Rows || 512,
                        columns: metadata.columns || metadata.Columns || 512,
                        bitsAllocated: metadata.BitsAllocated || 8,
                        bitsStored: metadata.BitsStored || 8,
                        highBit: metadata.HighBit || 7,
                        pixelRepresentation: metadata.PixelRepresentation || 0,
                        samplesPerPixel: metadata.samplesPerPixel || metadata.SamplesPerPixel || 1,
                        photometricInterpretation: metadata.PhotometricInterpretation || 'RGB',
                        pixelSpacing: metadata.pixelSpacing || metadata.PixelSpacing
                    };
                }
                
                if (type === 'voiLutModule') {
                    return {
                        windowCenter: metadata.windowCenter || metadata.WindowCenter || [128],
                        windowWidth: metadata.windowWidth || metadata.WindowWidth || [256]
                    };
                }
                
                if (type === 'modalityLutModule') {
                    return {
                        rescaleSlope: metadata.rescaleSlope || metadata.RescaleSlope || 1,
                        rescaleIntercept: metadata.rescaleIntercept || metadata.RescaleIntercept || 0,
                        rescaleType: metadata.RescaleType || 'US'
                    };
                }
                
                if (type === 'generalSeriesModule') {
                    return {
                        modality: metadata.Modality || 'US',
                        seriesInstanceUID: metadata.SeriesInstanceUID || 'AHI_SERIES_' + Date.now(),
                        seriesNumber: metadata.SeriesNumber || 1,
                        seriesDescription: metadata.SeriesDescription || 'AHI Image'
                    };
                }
            }
        }
        return undefined;
    });
}

// Load AHI image for Cornerstone3D
function loadAHIImageCS3D(imageId) {
    // Return the object that Cornerstone3D expects
    return {
        promise: new Promise((resolve, reject) => {
            try {
                // Parse the image ID
                const parts = imageId.split(':')[1].split('/');
                const imageSetId = parts[0];
                const frameId = parts[1];

                // Get the cached data
                const cachedData = window.ahiImageCache && window.ahiImageCache[imageId];
                if (!cachedData) {
                    reject(new Error('Image data not found in cache'));
                    return;
                }

                const { pixelData, metadata } = cachedData;

                // Create the image object for Cornerstone3D
                const image = {
                    imageId: imageId,
                    minPixelValue: metadata.minPixelValue || 0,
                    maxPixelValue: metadata.maxPixelValue || 255,
                    slope: metadata.rescaleSlope || metadata.RescaleSlope || 1,
                    intercept: metadata.rescaleIntercept || metadata.RescaleIntercept || 0,
                    windowCenter: metadata.windowCenter || metadata.WindowCenter || 128,
                    windowWidth: metadata.windowWidth || metadata.WindowWidth || 256,
                    getPixelData: () => pixelData,
                    rows: metadata.rows || metadata.Rows || 512,
                    columns: metadata.columns || metadata.Columns || 512,
                    height: metadata.rows || metadata.Rows || 512,
                    width: metadata.columns || metadata.Columns || 512,
                    color: metadata.color || metadata.samplesPerPixel > 1,
                    rgba: metadata.samplesPerPixel === 4,
                    columnPixelSpacing: metadata.pixelSpacing ? metadata.pixelSpacing[0] : 1,
                    rowPixelSpacing: metadata.pixelSpacing ? metadata.pixelSpacing[1] : 1,
                    sizeInBytes: pixelData.byteLength,
                    spacing: metadata.pixelSpacing || [1, 1],
                    origin: metadata.imagePositionPatient || metadata.ImagePositionPatient || [0, 0, 0],
                    direction: metadata.imageOrientationPatient || metadata.ImageOrientationPatient || [1, 0, 0, 0, 1, 0],
                    // Cornerstone3D specific properties
                    scalarData: pixelData,
                    metadata: {
                        Rows: metadata.rows || metadata.Rows || 512,
                        Columns: metadata.columns || metadata.Columns || 512,
                        PixelSpacing: metadata.pixelSpacing || metadata.PixelSpacing || [1, 1],
                        ImageOrientationPatient: metadata.imageOrientationPatient || metadata.ImageOrientationPatient || [1, 0, 0, 0, 1, 0],
                        ImagePositionPatient: metadata.imagePositionPatient || metadata.ImagePositionPatient || [0, 0, 0],
                        WindowCenter: metadata.windowCenter || metadata.WindowCenter || 128,
                        WindowWidth: metadata.windowWidth || metadata.WindowWidth || 256,
                        RescaleSlope: metadata.rescaleSlope || metadata.RescaleSlope || 1,
                        RescaleIntercept: metadata.rescaleIntercept || metadata.RescaleIntercept || 0,
                        PhotometricInterpretation: metadata.PhotometricInterpretation || 'RGB',
                        SamplesPerPixel: metadata.samplesPerPixel || metadata.SamplesPerPixel || 1
                    }
                };

                resolve(image);
            } catch (error) {
                reject(error);
            }
        }),
        cancelFn: undefined
    };
}

// Create AHI image ID for Cornerstone3D
export function createAHIImageIdCS3D(imageSetId, frameId) {
    return `ahi:${imageSetId}/${frameId}`;
}

// Setup Cornerstone3D viewport for AHI images
export function setupAHIViewport(element, imageId, renderingEngineId = 'ahiRenderingEngine') {
    return new Promise(async (resolve, reject) => {
        try {
            // Create or get rendering engine
            let renderingEngine;
            try {
                renderingEngine = cache.getRenderingEngine(renderingEngineId);
            } catch (e) {
                renderingEngine = new RenderingEngine(renderingEngineId);
            }

            // Create viewport
            const viewportId = element.id || 'ahiViewport';
            element.id = viewportId;

            const viewportInput = {
                viewportId,
                type: csEnums.ViewportType.STACK,
                element,
                defaultOptions: {
                    background: [0, 0, 0]
                }
            };

            renderingEngine.enableElement(viewportInput);

            // Get the viewport
            const viewport = renderingEngine.getViewport(viewportId);

            // Set the image stack
            await viewport.setStack([imageId]);

            // Render the image
            viewport.render();

            console.log('AHI viewport setup complete');
            resolve({ renderingEngine, viewport });

        } catch (error) {
            console.error('Error setting up AHI viewport:', error);
            reject(error);
        }
    });
}

// Setup tool group for AHI measurements
export function setupAHIToolGroup(viewportId, toolGroupId = 'ahiToolGroup') {
    try {
        // Remove existing tool group if any
        try {
            ToolGroupManager.destroyToolGroup(toolGroupId);
        } catch (e) {
            // Tool group doesn't exist, that's fine
        }

        // Create new tool group
        const toolGroup = ToolGroupManager.createToolGroup(toolGroupId);

        // Add tools to the group
        toolGroup.addTool(WindowLevelTool.toolName);
        toolGroup.addTool(PanTool.toolName);
        toolGroup.addTool(ZoomTool.toolName);
        toolGroup.addTool(LengthTool.toolName);
        toolGroup.addTool(EllipticalROITool.toolName);
        toolGroup.addTool(RectangleROITool.toolName);
        toolGroup.addTool(AngleTool.toolName);
        toolGroup.addTool(ProbeTool.toolName);
        toolGroup.addTool(BidirectionalTool.toolName);

        // Set initial active tool
        toolGroup.setToolActive(WindowLevelTool.toolName, {
            bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }]
        });

        // Set other tools as passive
        toolGroup.setToolPassive(LengthTool.toolName);
        toolGroup.setToolPassive(EllipticalROITool.toolName);
        toolGroup.setToolPassive(AngleTool.toolName);
        toolGroup.setToolPassive(ProbeTool.toolName);
        toolGroup.setToolPassive(BidirectionalTool.toolName);

        // Add viewport to tool group with proper rendering engine ID
        toolGroup.addViewport(viewportId, 'ahiRenderingEngine');

        console.log('AHI tool group setup complete');
        return toolGroup;

    } catch (error) {
        console.error('Error setting up AHI tool group:', error);
        throw error;
    }
}

// Activate measurement tool
export function activateAHIMeasurementTool(toolGroup, toolName) {
    try {
        // Map of our tool names to Cornerstone tool names
        const toolMapping = {
            'WindowLevel': WindowLevelTool.toolName,
            'Pan': PanTool.toolName,
            'Zoom': ZoomTool.toolName,
            'length': LengthTool.toolName,
            'area': EllipticalROITool.toolName,
            'ellipse': EllipticalROITool.toolName,
            'angle': AngleTool.toolName,
            'probe': ProbeTool.toolName,
            'bidirectional': BidirectionalTool.toolName
        };

        // Deactivate all tools
        Object.values(toolMapping).forEach(csToolName => {
            toolGroup.setToolPassive(csToolName);
        });

        // Activate the selected tool
        const cornerstoneToolName = toolMapping[toolName];
        if (cornerstoneToolName) {
            toolGroup.setToolActive(cornerstoneToolName, {
                bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }]
            });
            console.log(`Activated AHI tool: ${cornerstoneToolName}`);
            return true;
        }

        return false;
    } catch (error) {
        console.error('Error activating AHI measurement tool:', error);
        return false;
    }
}

// Clean up AHI viewport
export function cleanupAHIViewport(renderingEngine, toolGroupId) {
    try {
        if (renderingEngine) {
            renderingEngine.destroy();
        }
        
        if (toolGroupId) {
            try {
                ToolGroupManager.destroyToolGroup(toolGroupId);
            } catch (e) {
                // Tool group might not exist
            }
        }
    } catch (error) {
        console.error('Error cleaning up AHI viewport:', error);
    }
}

export default {
    registerAHIImageLoaderCS3D,
    createAHIImageIdCS3D,
    setupAHIViewport,
    setupAHIToolGroup,
    activateAHIMeasurementTool,
    cleanupAHIViewport
};
